/**
 * Utility functions for handling timezone conversions and date formatting
 */

/**
 * Parse a Date.toString()-like string and extract the date with timezone offset applied.
 * Returns a Date object representing the date in the specified timezone.
 * Example input: "Mon Nov 03 2025 22:00:00 GMT+0200 (Eastern European Standard Time)"
 * Returns: Date object for Nov 4, 2025 (after applying +0200 offset)
 */
export function parseDateWithTimezone(dateStr: string): Date | null {
  // Step 1: Match input string with regex pattern
  const regex =
    /^(?:\w{3})\s(\w{3})\s(\d{1,2})\s(\d{4})\s(\d{2}):(\d{2}):(\d{2})\sGMT([+-]\d{4})/;
  const match = regex.exec(dateStr);

  if (match) {
    const [, monStr, dayStr, yearStr, hourStr, minStr, secStr, gmtOffsetStr] =
      match;

    // Step 2: Month mapping
    const monthMap: Record<string, number> = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    const month = monthMap[monStr];

    // Step 3: Convert string components to numbers
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minStr, 10);
    const second = parseInt(secStr, 10);

    // Step 4: Compute UTC time from components
    const baseUtcMs = Date.UTC(year, month, day, hour, minute, second);

    // Step 5: Handle GMT offset (e.g., +0200 or -0530)
    const sign = gmtOffsetStr.startsWith("-") ? -1 : 1;
    const offsetHours = parseInt(gmtOffsetStr.slice(1, 3), 10);
    const offsetMinutes = parseInt(gmtOffsetStr.slice(3, 5), 10);
    const totalOffsetMinutes = sign * (offsetHours * 60 + offsetMinutes);

    // Step 6: Apply timezone offset to UTC timestamp
    const zonedMs = baseUtcMs + totalOffsetMinutes * 60000;

    const finalDate = new Date(zonedMs);

    return finalDate;
  }

  // Step 7: If regex failed, fallback to native Date parsing with timezone offset addition
  const fallbackDate = new Date(dateStr);

  // Get the timezone offset from the original date and add it to the date
  const timezoneOffsetMinutes = fallbackDate.getTimezoneOffset();

  // Add the timezone offset to the date (getTimezoneOffset returns negative for positive timezones)
  const adjustedTime = fallbackDate.getTime() - timezoneOffsetMinutes * 60000;
  const adjustedTempDate = new Date(adjustedTime);

  // Create a clean date without timezone information using individual components
  const cleanDate = new Date(
    adjustedTempDate.getFullYear(),
    adjustedTempDate.getMonth(),
    adjustedTempDate.getDate(),
    adjustedTempDate.getHours(),
    adjustedTempDate.getMinutes(),
    adjustedTempDate.getSeconds(),
    adjustedTempDate.getMilliseconds()
  );

  return cleanDate;
}

/**
 * Gets the user's current timezone
 * @returns The user's timezone string (e.g., "America/New_York")
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Converts a UTC date string to the user's local timezone
 * @param utcDateString - UTC date string (e.g., "2025-11-03T22:00:00Z")
 * @returns Date object in user's local timezone
 */
export const convertUTCToUserTimezone = (utcDateString: string): Date => {
  return new Date(utcDateString);
};

/**
 * Converts a UTC date string to a specific timezone
 * @param utcDateString - UTC date string (e.g., "2025-11-03T22:00:00Z")
 * @param timezone - Target timezone (e.g., "America/New_York")
 * @returns Date object converted to the specified timezone
 */
export const convertUTCToTimezone = (
  utcDateString: string,
  timezone: string
): Date => {
  const date = new Date(utcDateString);
  // Create a new date object that represents the same moment in the target timezone
  return new Date(date.toLocaleString("en-US", { timeZone: timezone }));
};

/**
 * Formats a date in the user's local timezone
 * @param utcDateString - UTC date string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string in user's timezone
 */
export const formatDateInUserTimezone = (
  utcDateString: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utcDateString);
  const userTimezone = getUserTimezone();

  return date.toLocaleDateString("en-US", {
    timeZone: userTimezone,
    ...options,
  });
};

/**
 * Formats a time in the user's local timezone
 * @param utcDateString - UTC date string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string in user's timezone
 */
export const formatTimeInUserTimezone = (
  utcDateString: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utcDateString);
  const userTimezone = getUserTimezone();

  return date
    .toLocaleTimeString("en-US", {
      timeZone: userTimezone,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...options,
    })
    .toLowerCase();
};

/**
 * Formats a complete date and time in the user's local timezone
 * @param utcDateString - UTC date string
 * @returns Formatted date and time string in user's timezone
 */
export const formatDateTimeInUserTimezone = (utcDateString: string): string => {
  const date = new Date(utcDateString);
  const userTimezone = getUserTimezone();

  return date.toLocaleString("en-US", {
    timeZone: userTimezone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Gets date label (Today, Tomorrow, or day name) for a UTC date in user's timezone
 * @param utcDateString - UTC date string
 * @returns Object with label and display strings
 */
export const getDateLabelInUserTimezone = (
  utcDateString: string
): { label: string; display: string } => {
  const date = new Date(utcDateString);
  const userTimezone = getUserTimezone();

  // Get current date in user's timezone
  const now = new Date();
  const today = new Date(
    now.toLocaleDateString("en-CA", { timeZone: userTimezone })
  );
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Get session date in user's timezone
  const sessionDateStr = date.toLocaleDateString("en-CA", {
    timeZone: userTimezone,
  });
  const sessionDate = new Date(sessionDateStr);

  if (sessionDate.getTime() === today.getTime()) {
    return {
      label: "Today",
      display: formatDateInUserTimezone(utcDateString, {
        month: "short",
        day: "numeric",
      }),
    };
  } else if (sessionDate.getTime() === tomorrow.getTime()) {
    return {
      label: "Tomorrow",
      display: formatDateInUserTimezone(utcDateString, {
        month: "short",
        day: "numeric",
      }),
    };
  } else {
    return {
      label: formatDateInUserTimezone(utcDateString, { weekday: "short" }),
      display: formatDateInUserTimezone(utcDateString, {
        month: "short",
        day: "numeric",
      }),
    };
  }
};

/**
 * Formats a session time range in the user's timezone
 * @param utcDateString - UTC date string for session start
 * @param durationMinutes - Duration in minutes
 * @returns Formatted time range string (e.g., "12:00pm - 1:30pm")
 */


/**
 * Gets the current date and time in the user's timezone
 * @returns Date object representing current time in user's timezone
 */
export const getCurrentDateInUserTimezone = (): Date => {
  const userTimezone = getUserTimezone();
  const now = new Date();
  return new Date(now.toLocaleString("en-US", { timeZone: userTimezone }));
};

/**
 * Checks if a UTC date is in the past relative to user's timezone
 * @param utcDateString - UTC date string
 * @returns True if the date is in the past
 */
export const isDateInPast = (utcDateString: string): boolean => {
  const date = new Date(utcDateString);
  const now = getCurrentDateInUserTimezone();
  return date < now;
};
